import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// El acceso de clientes es sin contraseña (enlace por correo), así que la sesión se
// guarda en localStorage para que sobreviva a cerrar la pestaña o el navegador, y el
// backend la renueva en cada visita (/auth/me). Antes vivía en sessionStorage.
export const USER_TOKEN_KEY = '_ut';

const readStoredToken = () => {
  try {
    const token = localStorage.getItem(USER_TOKEN_KEY) || sessionStorage.getItem(USER_TOKEN_KEY);
    // Migrar sesiones abiertas con la versión anterior
    if (token) {
      localStorage.setItem(USER_TOKEN_KEY, token);
      sessionStorage.removeItem(USER_TOKEN_KEY);
    }
    return token;
  } catch (e) {
    return null;
  }
};

const storeToken = (token) => {
  try {
    if (token) {
      localStorage.setItem(USER_TOKEN_KEY, token);
    } else {
      localStorage.removeItem(USER_TOKEN_KEY);
      sessionStorage.removeItem(USER_TOKEN_KEY);
    }
  } catch (e) {
    // Almacenamiento bloqueado (p. ej. modo privado): la sesión dura lo que la pestaña
  }
};

export const hasStoredUserToken = () => !!readStoredToken();

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe usarse dentro de AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const tokenRef = useRef(null);

  useEffect(() => {
    const verifyAuth = async () => {
      tokenRef.current = readStoredToken();

      if (!tokenRef.current) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/auth/me`, {
          headers: {
            'Authorization': `Bearer ${tokenRef.current}`
          }
        });

        if (response.ok) {
          const { token: refreshedToken, ...userData } = await response.json();
          // Token renovado: la sesión se extiende con cada visita
          if (refreshedToken) {
            tokenRef.current = refreshedToken;
            storeToken(refreshedToken);
          }
          if (userData?.email) {
            localStorage.setItem('alphatica_customer_email', userData.email);
          }
          setUser(userData);
        } else if (response.status === 401 || response.status === 403) {
          // Solo cerrar sesión si el backend la rechaza; un error de red no debe sacarlos
          tokenRef.current = null;
          storeToken(null);
        }
      } catch (error) {
        console.error('Error verifying auth:', error);
      } finally {
        setLoading(false);
      }
    };

    verifyAuth();
  }, []);

  // Adopt a session established by a call outside this context (the emailed
  // verification or login link, which returns a token/user).
  const setSession = useCallback((token, sessionUser) => {
    if (!token || !sessionUser) return;
    tokenRef.current = token;
    storeToken(token);
    if (sessionUser.email) {
      localStorage.setItem('alphatica_customer_email', sessionUser.email);
    }
    setUser(sessionUser);
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${tokenRef.current}`
        }
      });
    } catch (error) {
      console.error('Error during logout:', error);
    }
    tokenRef.current = null;
    storeToken(null);
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    token: tokenRef.current,
    logout,
    setSession,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || false,
  }), [user, loading, logout, setSession]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
